import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import styles from './Changelog.module.css';

interface ChangelogEntry {
  date: string;
  version: string;
  categories: {
    features?: string[];
    bugs?: string[];
    improvements?: string[];
    backend?: string[];
    database?: string[];
  };
}

const changelogData: ChangelogEntry[] = [
  {
    date: '2026-10-09',
    version: 'v1.28',
    categories: {
      features: [
        'Updated all ship, component, weapon, and crafting data for Star Citizen Alpha 4.10.2',
        'Constellation Mk V Centaurus and Skylark added as upcoming ships',
        'The classic Constellation line is now the Constellation Mk IV, matching its new in-game name',
      ],
      improvements: [
        'Ships that go straight to release now show a designed "artwork coming soon" card until their official art is published',
      ],
      database: [
        'Full re-sync from Alpha 4.10.2: 1,619 crafting blueprints (up from 1,608), refreshed mission rewards, mining and weapon data',
      ],
    },
  },
  {
    date: '2026-08-26',
    version: 'v1.27',
    categories: {
      features: [
        'Updated all ship, component, weapon, and crafting data for Star Citizen Alpha 4.10',
        'S-65 Stingray added — the new Kruger Intergalactic medium fighter',
      ],
      improvements: [
        'New ships now pick up their artwork correctly instead of showing a placeholder',
        'Crafting blueprint categories restored — some blueprints were showing as uncategorised',
      ],
      database: [
        'Full re-sync from Alpha 4.10: 1,608 crafting blueprints (up from 1,598), 154 reward pools, and refreshed weapon damage',
      ],
    },
  },
  {
    date: '2026-07-16',
    version: 'v1.26',
    categories: {
      features: [
        'Updated all ship, component, weapon, and crafting data for Star Citizen Alpha 4.9',
        "Basher added — the new Grey's Market ship",
      ],
      improvements: [
        'Distortion weapons now show their real damage — they previously displayed 0',
        'Weapon damage refreshed from the 4.9 game files; many values were several patches out of date',
        'Ship artwork updated across the fleet, and newly added ships now arrive with images',
        'In-game ship prices refreshed to current values',
      ],
      database: [
        'Full re-sync of ships, components, hardpoints, personal items, and 1,598 crafting blueprints from the Alpha 4.9 patch',
      ],
    },
  },
  {
    date: '2026-06-27',
    version: 'v1.25',
    categories: {
      features: [
        'Updated all ship, component, weapon, and crafting data for Star Citizen Alpha 4.8.2',
        'Aurora Mk II added — the earlier Aurora variants (CL, ES, LN, LX, MR) remain available for current owners',
        'Gatac Railen is now flight-ready',
        'Esperia Prowler Utility added',
      ],
      improvements: [
        'Ship cargo capacities recomputed directly from the 4.8.2 game files for accuracy',
        'In-game ship prices refreshed to current values — many ships were showing outdated prices',
      ],
      database: [
        'Full re-sync of ships, components, hardpoints, personal items, and 1,561 crafting blueprints from the Alpha 4.8.2 patch',
      ],
    },
  },
  {
    date: '2026-04-16',
    version: 'v1.24',
    categories: {
      features: [
        'Crafting Blueprints — full crafting system with 1045 blueprints, material requirements, and quality stat modifiers',
        'Quality Sliders — adjust quality (0–1000) per material slot to see interpolated stat effects in real time',
        'Crafting Planner — add blueprints to a planner, set quantities, and view aggregated material totals',
        'Mission Reward Pools — browse which missions reward which blueprint pools, organized by faction',
        'Sub-category Filters — filter blueprints by weapon type (Sniper, Pistol, SMG) or armor piece (Helmet, Arms, Legs, Torso)',
        'Blueprint Images — wiki-sourced images displayed as card banners and modal hero images',
        'Dismantle Stats — view dismantle time and recovery efficiency for each blueprint',
      ],
      improvements: [
        'Image matching algorithm hardened — color and body-part words no longer cause cross-item image contamination',
        'Images centered with object-fit: contain for consistent display across all card sizes',
        'Hover tooltip on images: "Image may not be exact to variant" for shared variant images',
        'Icon Legend dropdown to explain category icons on blueprint cards',
      ],
      backend: [
        'CraftingImageResolutionService — resolves wiki images via MediaWiki API with strict relevance filtering',
        'Crafting API endpoints: blueprints (paginated, filterable, sortable), sub-categories, pools, blueprint detail',
        'Sync pipeline extracts crafting data from P4K game files and resolves localized display names',
      ],
      database: [
        'CraftingBlueprints table with image URL, dismantle stats, sub-category, and full ingredient/modifier schema',
        'CraftingPools and CraftingPoolEntries tables for mission reward pool tracking',
      ],
    },
  },
  {
    date: '2026-02-18',
    version: 'v1.23',
    categories: {
      bugs: [
        'Fixed Constellation Taurus cargo capacity showing 6 SCU instead of correct 174 SCU',
        'Fixed 890 Jump cargo capacity showing 100 SCU instead of correct 388 SCU',
        'Fixed Constellation Phoenix cargo capacity showing null instead of correct 80 SCU',
        'Fixed Hermes cargo capacity showing null instead of correct 144 SCU',
        'Fixed Perseus cargo capacity showing null instead of correct 96 SCU',
        'Fixed Argo Raft cargo capacity showing 96 SCU instead of correct 192 SCU',
        'Fixed Argo SRV cargo capacity showing null instead of correct 12 SCU',
        'Fixed Misc Fortune cargo capacity showing null instead of correct 10 SCU',
        'Fixed Salvation cargo capacity showing null instead of correct 6 SCU',
      ],
      backend: [
        'Ship parser now scans cargogrid directory for grids absent from ship entity loadout XML',
        'Several ships had main cargo grids missing from their loadout definitions — parser now detects and includes them',
        'Added exclusion list to prevent variant-specific grids (Retaliator modules, Freelancer DUR/MAX) from being incorrectly counted',
      ],
    },
  },
  {
    date: '2025-12-18',
    version: 'v1.22',
    categories: {
      features: [
        'Ship Database Public Access - Ship Database now prominently featured as a free community resource',
        'Community Resources Section - New sidebar section highlighting publicly accessible features',
        'Bombs Category - Ship loadout now displays bomb racks with loaded bombs in hierarchical view',
        'Homepage Ship Database Promo - New card promoting the Ship Database on the homepage',
      ],
      improvements: [
        'Ship Database URL changed from /ships to /ship-database for clearer branding',
        'Legacy /ships URLs automatically redirect to new /ship-database path',
        'Community banner added to Ship Database pages with "Powered by TFIC" messaging',
        'Disclaimer footer clarifies unofficial fan project status and CIG attribution',
        'Public badges in sidebar indicate Ship/Item Database are freely accessible',
        'Bombs display with rack → bombs hierarchy matching missiles section style',
      ],
      bugs: [
        'Fixed Tractor Beam display - SRV no longer incorrectly shows "Mining Arm"',
        'Fixed ship navigation links after URL route change',
        'Fixed bomb detection to exclude missile racks from bombs category',
      ],
    },
  },
  {
    date: '2025-12-17',
    version: 'v1.21',
    categories: {
      features: [
        'Enhanced Tractor Beam Detection - Improved identification of tractor beams across all ship types',
        'Tractor Beam Category Improvements - Better handling of tractor turrets, consoles, and arm types',
      ],
      improvements: [
        'Tractor beams detected by multiple patterns: item name, slot name, localized name, item type',
        'Loaded items checked for tractor beam components (e.g., turrets with tractor equipment)',
        'TRAC_ prefix recognition for tractor beam items',
        'Better exclusion of tractor beams from weapons and turrets categories',
      ],
      bugs: [
        'Fixed tractor turrets appearing in both Turrets and Tractor Beams sections',
        'Fixed empty tractor beam slots showing in component lists',
      ],
    },
  },
  {
    date: '2025-12-16',
    version: 'v1.20',
    categories: {
      features: [
        'Turret Gun Count - Turrets now track how many weapon positions they have (e.g., Hammerhead turrets = 4 guns)',
        'FTL Drives Category - Quantum Drives and Jump Drives unified in a single "FTL Drives" section with type badges',
        'Engineering Tab - NEW! Comprehensive power budget analysis showing idle power consumption by system',
        'Power Triangle Context - Engineering tab explains how power management works in SC Alpha 4.5',
      ],
      improvements: [
        'Jump Drives now visible as separate component cards alongside Quantum Drives',
        'Type badges distinguish "Quantum" (purple) vs "Jump" (cyan) drives',
        'Power consumption breakdown shows shields, coolers, thrusters, weapons, radar, avionics, life support',
        'Power utilization bar with color coding (green < 70%, yellow 70-90%, red > 90%)',
      ],
      bugs: [
        'Fixed Jump Drives being incorrectly hidden from ship components',
        'Fixed Quantum Fuel Tanks appearing in FTL Drives section instead of fuel category',
        'Removed inaccurate "1 gun" badge from turret cards (was counting weapon types, not positions)',
      ],
      backend: [
        'Added TurretGunCount field to ItemDefinition model and sync parser',
        'Parser extracts gun count from P4K turret XML SItemPortDef elements',
        'Created comprehensive ShipDetailV2 architecture documentation',
      ],
    },
  },
  {
    date: '2025-12-16',
    version: 'v1.19',
    categories: {
      features: [
        'Ship Detail Page V2 - Complete redesign with modern tabbed interface',
        'Hero Section - Full-width ship image with production status, size, and role badges',
        'Quick Stats Bar - At-a-glance DPS, Shield HP, Max Speed, Cargo, Crew, and Power Output',
        'Overview Tab - Ship specifications including dimensions, mobility, fuel reserves, and description',
        'Combat Tab - Weapons DPS breakdown by size, missiles/torpedoes with damage, shields, and armor system',
        'Systems Tab - Power generation, thermal management, and quantum drive details',
        'Loadout Tab - Full equipment loadout with expandable categories and nested component display',
      ],
      improvements: [
        'Weapon breakdown shows DPS contribution by weapon size (S1-S9)',
        'Missile breakdown shows count and damage by size with Torpedo distinction (S9+)',
        'Armor system displays damage reduction percentages for Physical, Energy, and Distortion',
        'Component cards show size badges, manufacturer, and relevant stats',
        'Nested items display (weapons on mounts, missiles in racks, modules on mining heads)',
        'Category accordions with item counts and color-coded icons',
      ],
      bugs: [
        'Fixed duplicate components appearing from loadedItems hierarchy',
        'Fixed internal components (controllers, relays, seats) showing in loadout',
        'Fixed missile turrets appearing in both Turrets and Missiles sections',
        'Fixed ore pods/storage incorrectly showing module slots',
      ],
    },
  },
  {
    date: '2025-12-15',
    version: 'v1.15',
    categories: {
      features: [
        'Armor Damage Resistance - Combat Overview now displays actual armor damage reduction percentages from game data',
        'Physical and Energy resistance values displayed (e.g., 20% physical, 35% energy for medium armor)',
        'Sensors & Targeting Category - Torpedo cameras and targeting systems now properly categorized',
        'Hull Health Display - Ship hull health now shown in Combat Overview defensive section',
      ],
      improvements: [
        'Cargo Capacity Always Visible - Ships now always show cargo capacity (0 SCU if no cargo)',
        'Interior Tractor Beam Stations - Operator stations properly hidden from component lists',
        'Component Categorization - Better organization of ship components into logical groups',
        'Combat Overview - Comprehensive defensive stats including shields, armor, and hull health',
      ],
      bugs: [
        'Fixed empty remote turrets appearing in turret lists',
        'Fixed torpedo camera turret appearing in wrong category',
        'Fixed interior tractor beam stations showing as separate components',
      ],
    },
  },
  {
    date: '2025-12-14',
    version: 'v1.14',
    categories: {
      features: [
        'Ship Components Card Layout - Complete redesign with modern card-based display for all ship components',
        'Point Defense Section - PDCs now displayed in their own dedicated category with teal accent',
        'Tractor Beams Section - Utility items like tractor beams have their own category',
        'Avionics & Computers Section - Renamed and expanded to include all computer systems',
        'Mounted Weapons Display - Turrets and gimbals now show their equipped weapons inline',
        'Loaded Ordnance Display - Missile racks show their loaded missiles with damage and lock time stats',
      ],
      improvements: [
        'Component cards show size tags with color-coded badges (S1-S3 green, S4-S6 amber, S7+ red)',
        'Slot positions displayed (Port, Starboard, Upper, Lower, etc.) for easier identification',
        'Weapon stats now show damage, fire rate, and range in compact pill format',
        'Shield stats display HP, regeneration rate, and recharge delay',
        'Power plants show output in Watts, coolers show cooling rate',
        'Manufacturer names displayed under component names',
        'Lock indicators shown for non-swappable components',
      ],
      bugs: [
        'Fixed blast doors incorrectly appearing in Shields section (Polaris)',
        'Fixed missile racks appearing in Weapons instead of Missiles & Torpedoes (890 Jump)',
        'Fixed internal components showing (AI modules, controllers, relays now hidden)',
        'Fixed generic gimbal mounts showing as "S3" in Turrets section',
        'Fixed camera turrets appearing in component lists',
        'Removed duplicate items that appeared both as hardpoints and loaded items',
      ],
    },
  },
  {
    date: '2025-12-13',
    version: 'v1.13',
    categories: {
      features: [
        'Ship Database Cleanup - Import process now automatically removes junk and duplicate ships',
        'Manufacturer Normalization - Consistent manufacturer names and codes across all ships',
        'Armor System - Database ready for armor stats (ArmorHealth, ArmorType)',
        'Ship Variant Images - Variants now inherit images from base ships (e.g., Carrack Expedition gets Carrack image)',
      ],
      improvements: [
        'Ship count reduced from 365 to 221 by removing paint variants, event ships, and duplicates',
        'Performance section ready to display Armor Health and Armor Type',
        'Ship detail page now shows armor-related stats when available',
      ],
      bugs: [
        'Fixed Anvil Aerospace showing twice in manufacturer filter',
        'Fixed Carrack Expedition missing hardpoints and stats',
        'Removed junk ships: Bengal, CSV, Spirit, and various duplicates',
      ],
    },
  },
  {
    date: '2025-12-12',
    version: 'v1.12',
    categories: {
      features: [
        'Ship Descriptions - Ship database now displays detailed descriptions for all ships',
        'Comprehensive ship information including role, focus, and production status',
        'Enhanced ship cards with manufacturer details and specifications',
      ],
      improvements: [
        'Ship database page updated with better layout for ship metadata',
        'Improved data display for ship characteristics and purpose',
      ],
    },
  },
  {
    date: '2025-12-11',
    version: 'v1.11',
    categories: {
      features: [
        'Server-Side Toast Tracking - Reward notifications now persist across sessions',
        'Toast notifications survive deployments, browser cache clears, and device switches',
        'Notifications tracked in database instead of browser localStorage',
        'User Timezone Selection - Set your timezone in profile settings',
        '16+ timezone options covering US, Europe, Asia, and Pacific regions',
        'Timezone displayed on profile for coordination and event planning',
        'Preferred Playtimes - Weekly availability schedule for better team coordination',
        'Set your typical play times for each day of the week',
        'Time range inputs with start/end times for each day',
        'Enable/disable specific days based on your availability',
        'Helps leadership plan events and missions around member schedules',
      ],
      improvements: [
        'Chat widget now displays "Registered users only" notice',
        'Weekly playtime schedule with responsive layout that wraps properly',
        'Time inputs stay within profile borders on all screen sizes',
        'Profile editing UI enhanced with timezone dropdown and schedule picker',
      ],
    },
  },
  {
    date: '2025-12-11',
    version: 'v1.10',
    categories: {
      features: [
        'Medical SOS Death Timer Countdown - Real-time remaining time calculation',
        'Shows both original timer and actual remaining time with color coding',
        'Green text for active timers, red "EXPIRED" warning for overdue tickets',
        'Applies to all views: My Ticket, Available Tickets, My Tickets History, and Manage All',
        'Calculates elapsed time since ticket creation and subtracts from original timer',
        'Position Capacity Tracking - Visual indicators for full positions',
        'Positions now track current holders vs maximum allowed slots',
        'Full positions appear grayed out with "Full (X/Y)" badge on departments page',
        'Users cannot apply to positions that have reached capacity',
        'Backend calculates current holders across primary, secondary, and tertiary position slots',
      ],
      improvements: [
        'Medical SOS displays now show format: "15 min (Original) | 5 min (Remaining)"',
        'Position cards show capacity status alongside tier requirements and commitment level',
        'Enhanced user experience with clear visual feedback for unavailable positions',
      ],
      bugs: [
        'Fixed tier application logic to use Org Level (earned points) instead of admin role',
        'Position applications now correctly check user points for tier requirements',
        'Error messages now display actual user org level for transparency',
      ],
    },
  },
  {
    date: '2025-12-11',
    version: 'v1.9',
    categories: {
      features: [
        'Discord Chat Integration - Real-time two-way sync between website and Discord',
        'Added live Discord chat widget on home page showing latest messages',
        'Implemented full-page chat interface accessible via sidebar navigation',
        'Messages sync bidirectionally: Discord → Website and Website → Discord',
        'Support for Discord image attachments displayed as clickable thumbnails',
        'Message deletion sync: Deleted Discord messages automatically removed from website',
        'Username matching: Discord users linked to website profiles when available',
        'Auto-refresh: Chat updates every 5 seconds (widget) and 3 seconds (full page)',
      ],
      improvements: [
        'Updated welcome banner with professional messaging about platform redesign',
        'Banner now includes information about data backup implementation',
        'Added AlertCircle icon to banner for better visual communication',
        'Rewards Activity widget now scrollable with 600px max height',
        'Chat widget properly sized at 450px to fit container without overflow',
        'Improved scroll behavior: Chat widget scrolls internally without affecting page layout',
      ],
      bugs: [
        'Fixed home page auto-scroll issue caused by chat widget scroll behavior',
        'Fixed chat widget container sizing and padding conflicts',
        'Fixed Rewards Activity feed losing scroll functionality',
      ],
    },
  },
  {
    date: '2025-12-10',
    version: 'v1.8',
    categories: {
      features: [
        'Contextual Help System - Interactive help buttons across all major features',
        'Added contextual help buttons (?) to 8 feature pages: Missions, Events, Departments, Positions, Knowledge Base, Feedback, Medical SOS, Org Points',
        'Help modal displays relevant KB articles with titles, categories, and authors',
        'Click any article to navigate directly to full content',
        'Department & Position-Specific Required Reading - Granular filtering for KB articles',
        'Articles can now be marked as required for specific departments or positions',
        'KB Admin: Checkbox grid for selecting multiple departments',
        'KB Admin: Added tooltips explaining all major form sections',
      ],
      bugs: [
        'Fixed department filtering for required reading articles',
        'Fixed Mission Points Display showing incorrect values',
        'Removed duplicate help tooltips on Mission Board',
      ],
      improvements: [
        'Department Roster Redesign: Shows "2/5 filled" or "3 members (Unlimited)" instead of listing individual avatars',
        'Added "OPEN" badge when position slots available',
        'Onboarding Checklist: Removed completed articles from "Read Required Articles" task',
        'Only shows incomplete articles with action-oriented bullet points',
        'Contextual Help Modal: Dark theme with smooth animations',
      ],
    },
  },
  {
    date: '2025-12-07',
    version: 'v1.7.3',
    categories: {
      features: [
        'Two-Phase Onboarding System - Separated pre-member and post-member checklists',
        'Pre-Member Onboarding (for Prospects): 3 external steps required before member approval',
        '  • Read Code of Conduct in modal with comprehensive guidelines',
        '  • Verify Discord Account through server verification channel',
        '  • Join TFIC Organization on RSI Website (MASTERLS)',
        'Post-Member Getting Started (for Members): 4 internal platform steps to get involved',
        '  • Complete Your Profile with avatar and game roles',
        '  • Join a Department that interests you',
        '  • RSVP to Your First Event to participate',
        '  • Earn Your First ORG Points through activity',
        'Automatic approval request: Completing pre-member checklist triggers appearance in "New Registrations" tab',
      ],
      improvements: [
        'Dashboard conditionally renders correct checklist based on user role (Prospect vs Member)',
        'Decoupled member approval workflow: Registration creates Prospect → Complete onboarding → Leadership approves → Member role',
      ],
    },
  },
  {
    date: '2025-12-07',
    version: 'v1.7.2',
    categories: {
      features: [
        'Enhanced User Onboarding Checklist - Expanded with external integration steps',
        'Added 4 new onboarding steps: Read Code of Conduct, Verify Discord Account, Join TFIC on RSI Website, Set Up Discord Channels & Roles',
        'External steps include clickable links to Discord rules, verification, and RSI org page',
        'Manual checkboxes for external steps (click to mark complete) with progress saved per user',
        'Checklist now covers complete member journey: Code of Conduct → Discord Verification → RSI Join → Discord Setup → Profile → Department → Event → Points',
        'Leadership Communication System - "My Inbox" improvements',
        'Renamed "Leadership Inbox" to "My Inbox" in sidebar navigation',
        'Added unread message count badge showing "(1)" format next to inbox link',
        'Moved "My Inbox" link directly under "Leader Dashboard" for better organization',
      ],
      improvements: [
        'Progress persists for seamless experience across sessions',
        'Updated welcome message to emphasize onboarding completion',
        'Auto-refreshes unread count every 30 seconds for real-time updates',
      ],
    },
  },
  {
    date: '2025-12-07',
    version: 'v1.7.1',
    categories: {
      features: [
        'Discord Bot Recurring Events Support - Fixed compatibility with updated event system',
        'Bot now generates all child occurrences in database when creating recurring events (for calendar visibility)',
        'Added automatic scheduler that checks every 5 minutes for next recurring event occurrences',
        'Bot posts only ONE Discord message at a time - posts next occurrence after previous one ends',
        'Scheduler automatically posts next occurrence to Discord after parent event expires',
        'Discord message tracking ensures each occurrence is only posted once',
      ],
      bugs: [
        'Fixed bot API endpoint to store recurrence metadata (recurrenceType, recurrenceInterval, recurrenceEndDate)',
      ],
      improvements: [
        'Full compatibility with daily, weekly, monthly, quarterly, and yearly recurring events',
        'Website calendar shows all future occurrences while Discord stays clean with one active post',
      ],
    },
  },
  {
    date: '2025-12-07',
    version: 'v1.7',
    categories: {
      features: [
        'Dashboard Overview Tab - Complete redesign with widget-based layout',
        'Added new Overview tab as default landing page with comprehensive dashboard widgets',
        'Quick Stats Widget: ORG Points, Active Missions, Completed Missions, Upcoming Events, Active Positions, and Last Login',
        'Recent Activity Timeline: Shows last 5 user activities including RSVPs, completed missions, and points earned',
        'Upcoming Events Widget: Displays next 3 events with countdown timers',
        'What\'s New Widget: Dynamic announcements from recent changelog entries (v1.6 Mission System, Member Directory, Dashboard improvements)',
        'Quick Actions Widget: Organized into logical groups (Organization: Events, Missions, Departments | Personal: Profile, Preferences, Settings)',
        'Getting Started Checklist: Only shows for incomplete items - tracks profile completion, department membership, event attendance, and first points earned',
        'Position Management Improvements',
        'Added department dropdown to position creation form with all 8 departments selectable',
      ],
      improvements: [
        'Empty state messages for stats with actionable hints (e.g., "Start earning!" for 0 points)',
        'Replaced all emoji icons with professional Lucide icons for consistent UI (BarChart3, Zap, Activity, Calendar, Bell, Clock, CheckCircle)',
        'Smart welcome message in profile header: Shows position/department if assigned, checklist prompt if incomplete, or nothing if all complete',
      ],
      bugs: [
        'Fixed position form department selector not working for new positions',
        'Department selector now fully functional for creating new positions',
      ],
    },
  },
  {
    date: '2025-12-06',
    version: 'v1.6',
    categories: {
      features: [
        'First implementation of the Mission System: discover missions, accept assignments, progress statuses, and track completion end-to-end',
        'Leader Missions tools: better overview of pending requests and assignment controls',
        'My Missions page refresh: clearer status badges, responsive layout, and simplified interactions',
        'Organization points transactions: initial support for rewards/adjustments tied to mission activity',
      ],
      bugs: [
        'Fixed legacy mission states and transitions so lists accurately reflect Accepted → In Progress → Completed',
      ],
      improvements: [
        'Streamlined mission acceptance flow with clearer actions and feedback',
        'Dashboard and Sidebar improvements: elevated mission indicators and cleaned-up navigation',
        'Medical SOS reliability polish',
      ],
    },
  },
  {
    date: '2025-12-05',
    version: 'v1.5',
    categories: {
      features: [
        'Member Directory - Complete member listing and search system',
        'Grid and list view toggle for member display',
        'Search functionality for username and position',
        'Department and role filtering with dropdowns',
        'Displays member avatars, positions, departments, and join dates',
        'Members-only access to directory',
        'Prospect Role System - New member tier for onboarding',
        'All new registrations start as Prospect',
        'Prospect option added to all role filters',
        'Role hierarchy: Prospect → Member → Leader → Admin',
        'Permission Security Fix - Real-time permission validation',
        'Revoked permissions now take effect immediately',
      ],
      bugs: [
        'Ensures revoked permissions take effect immediately (no cached access)',
        'Prevents security gaps from stale authentication data',
      ],
      improvements: [
        'Updated Members Directory with themed card-based layout matching other pages',
        'Consistent styling throughout the application',
        'Added result count display showing filtered vs total members',
      ],
    },
  },
  {
    date: '2025-12-04',
    version: 'v1.4',
    categories: {
      features: [
        'Leadership-Only Events - Tier-restricted events for leaders and admins',
        'Leadership visibility toggle on event creation/edit page',
        'Calendar styling: blue for regular events, orange for leadership events',
        'Leadership badge on event detail page indicating tier 3 & 4 restriction',
        'Unlimited RSVP Capacity - General attendance options support unlimited participants',
        'General RSVP options: Attending, Tentative, Not Attending (all unlimited)',
        'Standby and Spectator roles with unlimited capacity',
        'DateTime UX Improvements - Enhanced time selection experience',
        '15-minute increment steps for easier time selection',
        'Enhanced datetime picker with larger fonts and better visual feedback',
      ],
      bugs: [
        'Fixed timezone conversion bug when editing events',
      ],
      improvements: [
        'Capacity display shows "X attending" for unlimited roles instead of "X / 9999"',
      ],
    },
  },
  {
    date: '2025-12-04',
    version: 'v1.3',
    categories: {
      features: [
        'Medical SOS System - Complete medical emergency ticketing system',
        'Comprehensive Medical SOS page with tabbed interface',
        'Ticket submission form with CAPTCHA protection',
        'Real-time ticket status tracking (Pending, Claimed, Completed, Cancelled)',
        'Medic assignment system with claim/unclaim functionality',
        'Ticket management dashboard for medics with ETA and notes',
        'Ticket history view showing all past requests',
        '"Hostile Environment" and "PvP Risk Area" toggles for submissions',
        'Added placeholder links for future assistance features (Hauling, Ship SOS, Escort)',
        'New "Request Assistance" section in sidebar',
      ],
      improvements: [
        'Medical ticket management restricted to authorized positions only',
      ],
    },
  },
  {
    date: '2025-12-04',
    version: 'v1.2',
    categories: {
      features: [
        'Added dedicated EventDetail page with full event information and RSVP',
        'One-role-per-event RSVP enforcement',
        'Created event creation workflow',
      ],
      improvements: [
        'Enhanced department events modal with better layout',
        'Improved modal scroll capability',
        'Enhanced UI/UX across events system',
        'Implemented automated daily backups for data protection',
      ],
    },
  },
  {
    date: '2025-04-30',
    version: 'v1.1',
    categories: {
      features: [
        'Website goes live version 1.1',
        'adding leadership video section',
        'additional user management functionality added',
      ],
    },
  },
  {
    date: '2025-04-30',
    version: 'v1.0.9',
    categories: {
      features: [
        'Added user acct delete functionality',
        'Discord bot synchronization completed. This includes checking for users discord name on website for rsvp',
      ],
      improvements: [
        'more UI improvements added',
      ],
    },
  },
  {
    date: '2025-04-28',
    version: 'v1.0.8',
    categories: {
      bugs: [
        'Fixed data refresh on department page switching',
      ],
      improvements: [
        'Update Discord Bot with better event creation flow (update 1)',
      ],
    },
  },
  {
    date: '2025-04-28',
    version: 'v1.0.7',
    categories: {
      features: [
        'Added Video Sections to Departments',
      ],
      improvements: [
        'Polishing the Video management experience',
      ],
    },
  },
  {
    date: '2025-04-27',
    version: 'v1.0.6',
    categories: {
      features: [
        'Added Password Reset functionality',
      ],
      bugs: [
        'Discord User field now saves correctly',
      ],
    },
  },
  {
    date: '2025-04-27',
    version: 'v1.0.5',
    categories: {
      bugs: [
        'Fixed DiscordName editing not saving properly.',
      ],
      improvements: [
        'Improved sidebar mobile responsiveness and department alignment.',
        'Added CAPTCHA to Data Request Form to block spam.',
        'Refined Leader Dashboard user edit modal (editable Discord/Game Username).',
      ],
    },
  },
  {
    date: '2025-04-20',
    version: 'v1.0.4',
    categories: {
      improvements: [
        'Refined department application module.',
        'Mobile visual improvements across dashboard and events. More work to be done here',
        'Leader Dashboard now shows full position assignments correctly.',
      ],
    },
  },
  {
    date: '2025-04-15',
    version: 'v1.0.3',
    categories: {
      features: [
        'Integrated Discord bot RSVP syncing. More improvements to be done here',
        'Added department-specific event filters.',
      ],
    },
  },
];

const Changelog: React.FC = () => {
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  const toggleVersion = (version: string) => {
    setExpandedVersion(expandedVersion === version ? null : version);
  };

  const categoryLabels: Record<string, string> = {
    features: '✨ New Features',
    bugs: '🐛 Bug Fixes',
    improvements: '⚡ Improvements',
    backend: '🔧 Backend & API',
    database: '💾 Database',
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Changelog</h1>
        <p className={styles.subtitle}>
          Track all updates, new features, and improvements to the TFIC platform
        </p>
      </div>

      <div className={styles.versionsSection}>
        {changelogData.map((entry) => (
          <div key={entry.version} className={styles.versionCard}>
            <div
              className={styles.versionHeader}
              onClick={() => toggleVersion(entry.version)}
            >
              <div className={styles.versionHeaderLeft}>
                <div className={styles.versionHeaderText}>
                  <h3>{entry.version}</h3>
                  <span className={styles.versionDate}>{entry.date}</span>
                </div>
              </div>
              <div className={styles.versionHeaderRight}>
                {expandedVersion === entry.version ? (
                  <ChevronUp className={styles.chevron} />
                ) : (
                  <ChevronDown className={styles.chevron} />
                )}
              </div>
            </div>

            {expandedVersion === entry.version && (
              <div className={styles.versionContent}>
                {Object.entries(entry.categories).map(([category, items]) => (
                  items && items.length > 0 && (
                    <div key={category} className={styles.categorySection}>
                      <h4 className={styles.categoryTitle}>
                        {categoryLabels[category] || category}
                      </h4>
                      <ul className={styles.changeList}>
                        {items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Changelog;
